// "Includes…" section of the product editor. Lets an admin pick other products
// that are automatically brought along (at no charge) when this product is
// selected on the intake form. The relationship is one-way: configuring "A
// includes B" here says nothing about selecting B on its own.
//
// Draft shape (productEditor.includes): [{ included_product_id, option_ids }].
// When an included product has its own options, the admin can pin which of
// those options are captured — a single-select product offers one (or "Base
// product"), a multiple-select product offers any subset.
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Divider, Autocomplete, TextField, Chip, MenuItem,
  FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { setIncludes } from '../../../store/product-editor-slice.js';
import { optionsForProduct } from '../../../utils/catalog-helpers.js';

const ProductIncludesSection = () => {
  const dispatch = useDispatch();
  const { includes, productId, draft } = useSelector((s) => s.productEditor);
  const { products, options } = useSelector((s) => s.adminCatalog.data);

  // Any active product other than the one being edited can be included.
  const available = products
    .filter((p) => p.is_active && p.id !== productId)
    .sort((a, b) => a.name.localeCompare(b.name));

  const productById = new Map(products.map((p) => [p.id, p]));
  const selectedProducts = includes
    .map((e) => productById.get(e.included_product_id))
    .filter(Boolean);

  // Replace the whole set from the Autocomplete, preserving any captured
  // option_ids for products that stay selected.
  const handleProductsChange = (_e, picked) => {
    const prev = new Map(includes.map((e) => [e.included_product_id, e]));
    dispatch(setIncludes(picked.map((p) => (
      prev.get(p.id) ?? { included_product_id: p.id, option_ids: [] }
    ))));
  };

  const setEntryOptions = (includedProductId, optionIds) => {
    dispatch(setIncludes(includes.map((e) => (
      e.included_product_id === includedProductId ? { ...e, option_ids: optionIds } : e
    ))));
  };

  return (
    <>
      <Divider />
      <Box>
        <Typography
          component="span"
          variant="subtitle2"
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5,
          }}
        >
          <AccountTreeOutlinedIcon fontSize="small" color="action" />
          Includes
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {`Other products automatically added at no charge when ${
            draft.name?.trim() ? `“${draft.name.trim()}”` : 'this product'
          } is selected. This is one-way — it doesn’t make those products include this one.`}
        </Typography>

        <Autocomplete
          multiple
          options={available}
          value={selectedProducts}
          onChange={handleProductsChange}
          getOptionLabel={(p) => p.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderTags={(value, getTagProps) => value.map((p, index) => (
            <Chip label={p.name} size="small" {...getTagProps({ index })} key={p.id} />
          ))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Included products"
              placeholder={selectedProducts.length ? '' : 'Add a product…'}
            />
          )}
        />

        {includes.map((entry) => {
          const product = productById.get(entry.included_product_id);
          if (!product) return null;
          const productOptions = optionsForProduct(options, product.id)
            .filter((o) => o.is_active);
          if (productOptions.length === 0) return null;
          const isMultiple = product.selection_mode === 'multiple';

          return (
            <Box
              key={entry.included_product_id}
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: (t) => alpha(
                  t.palette.text.primary,
                  t.palette.mode === 'dark' ? 0.06 : 0.03,
                ),
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {`Captured option${isMultiple ? 's' : ''} for “${product.name}”`}
              </Typography>

              {isMultiple ? (
                <FormGroup>
                  {productOptions.map((opt) => (
                    <FormControlLabel
                      key={opt.id}
                      sx={{ m: 0 }}
                      control={(
                        <Checkbox
                          size="small"
                          sx={{ py: 0.25 }}
                          checked={entry.option_ids.includes(opt.id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...entry.option_ids, opt.id]
                              : entry.option_ids.filter((id) => id !== opt.id);
                            setEntryOptions(entry.included_product_id, next);
                          }}
                        />
                      )}
                      label={<Typography variant="body2">{opt.name}</Typography>}
                    />
                  ))}
                </FormGroup>
              ) : (
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={entry.option_ids[0] ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEntryOptions(entry.included_product_id, v === '' ? [] : [Number(v)]);
                  }}
                >
                  <MenuItem value="">Base product (no specific option)</MenuItem>
                  {productOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default ProductIncludesSection;
